import { useState, useEffect, useCallback, useRef } from "react";
import * as pharmaApi from "../services/pharmaApi";

/**
 * Hook to fetch customer-based product suggestions for PDV
 * Based on purchase history, patterns and preferences
 *
 * @param {string} customerId - The PharmaFlow customer ID
 * @param {object} options - Options like storeId, limit
 * @returns {Object} - { suggestions, loading, error, refetch, hasSuggestions, dismissedCount }
 */
const useCustomerSuggestions = (customerId, options = {}) => {
  const { storeId, limit = 10, enabled = true } = options;

  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [dismissed, setDismissed] = useState(new Set());
  const [lastFetchedId, setLastFetchedId] = useState(null);

  // Ref to track if component is mounted
  const mountedRef = useRef(true);

  const fetchSuggestions = useCallback(async () => {
    if (!customerId || !enabled) {
      setSuggestions([]);
      return;
    }

    // Avoid refetching for the same customer
    if (lastFetchedId === customerId) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const data = await pharmaApi.getCustomerSuggestions(customerId, {
        storeId,
        limit,
      });

      if (mountedRef.current) {
        setSuggestions(data.suggestions || []);
        setLastFetchedId(customerId);
        setDismissed(new Set()); // Reset dismissed when customer changes
      }
    } catch (err) {
      console.error("Error fetching customer suggestions:", err);
      if (mountedRef.current) {
        setError(err.message || "Erro ao buscar sugestões");
        setSuggestions([]);
      }
    } finally {
      if (mountedRef.current) {
        setLoading(false);
      }
    }
  }, [customerId, storeId, limit, enabled, lastFetchedId]);

  // Fetch when customer changes
  useEffect(() => {
    fetchSuggestions();
  }, [fetchSuggestions]);

  // Cleanup on unmount
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  // Dismiss a suggestion (hide it temporarily)
  const dismissSuggestion = useCallback((productId) => {
    setDismissed(prev => new Set([...prev, productId]));
  }, []);

  // Dismiss all suggestions
  const dismissAll = useCallback(() => {
    setDismissed(new Set(suggestions.map(s => s.id)));
  }, [suggestions]);

  // Reset dismissed suggestions
  const resetDismissed = useCallback(() => {
    setDismissed(new Set());
  }, []);

  // Force refetch (ignoring cache)
  const forceRefetch = useCallback(async () => {
    setLastFetchedId(null);
    await fetchSuggestions();
  }, [fetchSuggestions]);

  // Filter out dismissed suggestions
  const activeSuggestions = suggestions.filter(s => !dismissed.has(s.id));

  return {
    suggestions: activeSuggestions,
    allSuggestions: suggestions,
    loading,
    error,
    refetch: forceRefetch,
    hasSuggestions: activeSuggestions.length > 0,
    totalCount: suggestions.length,
    dismissedCount: dismissed.size,
    dismissSuggestion,
    dismissAll,
    resetDismissed,
  };
};

export default useCustomerSuggestions;
